import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, User, Mail, ArrowRight, Scissors, Paintbrush, Palette, Eye,
  Calendar, Users, Utensils, Heart, Globe, Leaf, ShoppingBag, Sparkles,
  CheckCircle2, Loader2, Clock, GraduationCap, Bell, CreditCard, X
} from "lucide-react";
import { getCategoryPreset } from "@/lib/categoryConfig";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Image-based category icons for all categories
const getIcon = (key) => {
  const preset = getCategoryPreset(key);
  return preset?.icon || Sparkles;
};

const CAT_META = {
  hair: { icon: getIcon("hair"), color: "bg-pink-500" }, makeup: { icon: getIcon("makeup"), color: "bg-purple-500" },
  nail: { icon: getIcon("nails"), color: "bg-red-500" }, nails: { icon: getIcon("nails"), color: "bg-red-500" },
  lash: { icon: getIcon("lashes"), color: "bg-purple-500" }, culinary: { icon: getIcon("culinary"), color: "bg-amber-500" },
  wellness: { icon: getIcon("wellness"), color: "bg-emerald-500" }, event: { icon: getIcon("events"), color: "bg-violet-500" },
  events: { icon: getIcon("events"), color: "bg-violet-500" }, instructor: { icon: getIcon("instructors"), color: "bg-indigo-500" },
  travel: { icon: getIcon("travel"), color: "bg-blue-500" }, dispensary: { icon: getIcon("dispensary"), color: "bg-green-600" },
  retail: { icon: getIcon("style"), color: "bg-gray-700" },
};

export default function PublicOnboardPage() {
  const navigate = useNavigate();
  const { category: urlCategory } = useParams();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan") || "trial";

  const [categories, setCategories] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedCat, setSelectedCat] = useState(urlCategory || "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({ company_name: "", first_name: "", last_name: "", email: "" });
  const [stripeLoading, setStripeLoading] = useState(false);

  // Waitlist popup state
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistCat, setWaitlistCat] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);

  useEffect(() => {
    fetch(`${API}/categories/public`).then(r => r.json()).then(data => {
      setCategories(data);
      // If URL has a category, auto-route based on status
      if (urlCategory) {
        const cat = data.find(c => c.slug === urlCategory);
        if (cat) {
          if (cat.launch_status === "coming_soon") {
            setWaitlistCat(cat);
            setWaitlistOpen(true);
          } else if (cat.launch_status !== "hidden") {
            setSelectedCat(urlCategory);
            setStep(2);
          }
        }
      }
    }).catch(() => {});
  }, [urlCategory]);

  const selectedCategory = categories.find(c => c.slug === selectedCat);
  const meta = CAT_META[selectedCat] || { icon: Sparkles, color: "bg-gray-800" };
  const Icon = meta.icon;

  const handleCategoryClick = (cat) => {
    if (cat.launch_status === "coming_soon") {
      setWaitlistCat(cat);
      setWaitlistEmail("");
      setWaitlistName("");
      setWaitlistDone(false);
      setWaitlistOpen(true);
    } else {
      // live or pilot - go straight to signup
      setSelectedCat(cat.slug);
      setStep(2);
    }
  };

  const handleWaitlistSubmit = async () => {
    if (!waitlistEmail) { toast.error("Enter your email"); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(waitlistEmail)) { toast.error("Please enter a valid email address"); return; }
    setWaitlistSubmitting(true);
    try {
      const res = await fetch(`${API}/categories/${waitlistCat.slug}/waitlist`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: waitlistEmail, name: waitlistName })
      });
      const data = await res.json();
      if (res.ok) {
        setWaitlistDone(true);
        toast.success("You're on the list!");
      } else {
        toast.error(data.detail || "Error joining waitlist");
      }
    } catch { toast.error("Connection error"); }
    setWaitlistSubmitting(false);
  };

  const handleSignup = async () => {
    if (!form.company_name || !form.first_name || !form.email) { toast.error("Fill required fields"); return; }
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) { toast.error("Please enter a valid email address"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/onboard/signup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, category: selectedCat, plan: planParam })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStep(3);
        toast.success("Account created! Set up your subscription.");
      } else {
        toast.error(data.detail || "Error");
      }
    } catch { toast.error("Failed"); }
    setSubmitting(false);
  };

  const handleStripeCheckout = async () => {
    if (!result) return;
    setStripeLoading(true);
    try {
      const res = await fetch(`${API}/subscription/stripe-trial`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: "solo", billing_cycle: "monthly",
          origin_url: window.location.origin,
          user_id: result.user_id, event_id: result.event_id || "",
        })
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Could not create checkout");
        setStripeLoading(false);
      }
    } catch {
      toast.error("Connection error");
      setStripeLoading(false);
    }
  };

  const handleSkipToFreeDashboard = () => {
    if (result?.magic_token) navigate(`/auth/magic?token=${result.magic_token}`);
  };

  // Waitlist Popup Dialog
  const WaitlistDialog = () => (
    <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
      <DialogContent className="max-w-sm" data-testid="waitlist-dialog">
        {!waitlistDone ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                {waitlistCat && (() => {
                  const cm = CAT_META[waitlistCat.slug] || { icon: Sparkles, color: "bg-gray-500" };
                  const WIcon = cm.icon;
                  return <div className={`w-10 h-10 rounded-lg ${cm.color} text-white flex items-center justify-center`}><WIcon className="w-5 h-5" /></div>;
                })()}
                <div>
                  <DialogTitle className="text-lg">{waitlistCat?.name} is Coming Soon</DialogTitle>
                  <p className="text-xs text-gray-500 mt-0.5">Be the first to know when it launches</p>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs text-gray-600">Category</Label>
                <Input value={waitlistCat?.name || ""} disabled className="mt-1 rounded-xl h-10 bg-gray-50" data-testid="waitlist-category-input" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Your Name</Label>
                <Input value={waitlistName} onChange={e => setWaitlistName(e.target.value)}
                  placeholder="First name" className="mt-1 rounded-xl h-10" data-testid="waitlist-name-input" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="email" value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)}
                    placeholder="you@example.com" className="pl-10 rounded-xl h-10" data-testid="waitlist-email-input" />
                </div>
              </div>
              <Button onClick={handleWaitlistSubmit} disabled={waitlistSubmitting}
                className="w-full rounded-xl bg-gray-900 hover:bg-gray-800 text-white h-11" data-testid="waitlist-submit-btn">
                {waitlistSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-2" /> Join the Waitlist</>}
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Bell className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold" data-testid="waitlist-success">You're on the list!</h3>
            <p className="text-sm text-gray-500 mt-1">We'll notify you when {waitlistCat?.name} launches.</p>
            <Button variant="outline" className="mt-4 rounded-xl" onClick={() => setWaitlistOpen(false)}>Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  // Step 1: Category Grid
  if (step === 1) {
    const visible = categories.filter(c => c.launch_status !== "hidden");
    const liveOrPilot = visible.filter(c => c.launch_status === "live" || c.launch_status === "pilot");
    const comingSoon = visible.filter(c => c.launch_status === "coming_soon");

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="onboard-title">Start Your Business on Phygital</h1>
            <p className="text-sm text-gray-500 mt-1">Choose your category to get started with a 60-day free trial</p>
          </div>

          {/* Live / Pilot categories */}
          {liveOrPilot.length > 0 && (
            <div className="mb-6">
              <div className="grid grid-cols-2 gap-3" data-testid="onboard-category-grid">
                {liveOrPilot.map(cat => {
                  const cm = CAT_META[cat.slug] || { icon: Sparkles, color: "bg-gray-500" };
                  const CIcon = cm.icon;
                  return (
                    <button key={cat.slug} onClick={() => handleCategoryClick(cat)}
                      className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-400 transition-all text-left"
                      data-testid={`onboard-cat-${cat.slug}`}>
                      <div className={`w-9 h-9 rounded-lg ${cm.color} text-white flex items-center justify-center shrink-0`}>
                        <CIcon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium">{cat.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Coming Soon categories */}
          {comingSoon.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-3 px-1">Coming Soon</p>
              <div className="grid grid-cols-2 gap-3" data-testid="onboard-coming-soon-grid">
                {comingSoon.map(cat => {
                  const cm = CAT_META[cat.slug] || { icon: Sparkles, color: "bg-gray-500" };
                  const CIcon = cm.icon;
                  return (
                    <button key={cat.slug} onClick={() => handleCategoryClick(cat)}
                      className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-200 bg-white/60 hover:border-blue-300 transition-all text-left opacity-80"
                      data-testid={`onboard-cat-${cat.slug}`}>
                      <div className={`w-9 h-9 rounded-lg ${cm.color} text-white flex items-center justify-center shrink-0 opacity-70`}>
                        <CIcon className="w-4 h-4" />
                      </div>
                      <div>
                        <span className="text-sm font-medium block">{cat.name}</span>
                        <span className="text-[10px] text-blue-600 font-medium">Join Waitlist</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <WaitlistDialog />
      </div>
    );
  }

  // Step 2: Signup Form
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-6">
            <div className={`w-12 h-12 rounded-xl ${meta.color} text-white flex items-center justify-center mx-auto mb-3`}>
              <Icon className="w-6 h-6" />
            </div>
            <h1 className="text-xl font-bold" data-testid="onboard-step2-title">Create Your {selectedCategory?.name || selectedCat} Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">60-day Solo free trial with card on file</p>
          </div>
          <Card>
            <CardContent className="p-5 space-y-4">
              <div>
                <Label className="text-xs text-gray-700">Business Name *</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                    placeholder="Your business name" className="pl-10 rounded-xl h-11" data-testid="onboard-company" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-700">First Name *</Label>
                  <Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="First" className="mt-1 rounded-xl h-10" data-testid="onboard-first" />
                </div>
                <div>
                  <Label className="text-xs text-gray-700">Last Name</Label>
                  <Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Last" className="mt-1 rounded-xl h-10" data-testid="onboard-last" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-700">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com" className="pl-10 rounded-xl h-11" data-testid="onboard-email" />
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
                  <Clock className="w-4 h-4" /> 60-Day Solo Free Trial
                </div>
                <p className="text-xs text-green-600 mt-1">Full Solo features for 60 days. Card required, $11/mo after trial. Cancel anytime.</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setStep(1); setSelectedCat(""); }} className="flex-1 rounded-xl">Back</Button>
                <Button onClick={handleSignup} disabled={submitting}
                  className="flex-1 rounded-xl bg-gray-900 hover:bg-gray-800 text-white" data-testid="onboard-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating...</> : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step 3: Stripe Checkout (after account created)
  if (step === 3 && result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2" data-testid="onboard-success">Account Created!</h1>
          <p className="text-sm text-gray-500 mb-6">Start your 60-day Solo trial by adding a card. You won't be charged until the trial ends.</p>
          <Card className="mb-4">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4 text-left">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Solo Plan - $11/mo</p>
                  <p className="text-xs text-gray-500">60 days free, then billed monthly</p>
                </div>
              </div>
              <ul className="text-xs text-gray-600 space-y-1.5 text-left mb-4">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" /> Unlimited client profiles</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" /> Full service menu with upsells</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" /> Inspiration gallery and looks</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" /> Analytics dashboard</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" /> Payment links (Venmo/CashApp/Zelle)</li>
              </ul>
              <Button onClick={handleStripeCheckout} disabled={stripeLoading}
                className="w-full rounded-xl bg-gray-900 hover:bg-gray-800 text-white h-12" data-testid="stripe-checkout-btn">
                {stripeLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe...</> : <><CreditCard className="w-4 h-4 mr-2" /> Start 60-Day Free Trial</>}
              </Button>
              <p className="text-[10px] text-gray-400 mt-2">Secure checkout powered by Stripe. Cancel anytime.</p>
            </CardContent>
          </Card>
          <button onClick={handleSkipToFreeDashboard} className="text-xs text-gray-400 hover:text-gray-600 underline" data-testid="skip-to-free">
            Skip and use Free tier (5 client limit)
          </button>
        </div>
      </div>
    );
  }

  return null;
}
